import { trapFocus, removeTrapFocus, lockScroll, unlockScroll, closeDrawer, debounce, motionReduced, setDrawerClosedAccessibility, setDrawerOpenAccessibility } from '@theme/theme-global';

const DEBOUNCE_MS = 300;
const RESULTS_ID = 'predictive-search-results';
const RECENT_SEARCHES_KEY = 'recent-searches';
const MAX_RECENT_SEARCHES = 5;
const PLACEHOLDER_TYPE_MS = 40;
const PLACEHOLDER_ERASE_MS = 25;
const PLACEHOLDER_HOLD_MS = 2000;

function parseRunningPlaceholderLines(raw) {
  if (!raw) return [];

  let value = raw;
  try {
    value = JSON.parse(raw);
  } catch {
    // Attribute may already be a plain string
  }

  if (typeof value !== 'string' || !value.trim()) return [];

  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

class RunningPlaceholder {
  constructor(input, linesSource) {
    this._input = input;
    this._defaultPlaceholder = input.getAttribute('placeholder') || '';
    this._lines = parseRunningPlaceholderLines(linesSource);
    this._locked = false;
    this._running = false;
    this._lineIndex = 0;
    this._charIndex = 0;
    this._timer = null;
    this._onPointerDown = () => this.lock();
    this._onKeyDown = (event) => {
      if (['Tab', 'Escape', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(event.key)) return;
      this.lock();
    };
  }

  bindLockListeners() {
    this._input.addEventListener('pointerdown', this._onPointerDown);
    this._input.addEventListener('keydown', this._onKeyDown);
  }

  unbindLockListeners() {
    this._input.removeEventListener('pointerdown', this._onPointerDown);
    this._input.removeEventListener('keydown', this._onKeyDown);
  }

  start() {
    this.stop();
    this._locked = false;

    if (!this._input) return;

    if (!this._lines.length) {
      this._input.placeholder = this._defaultPlaceholder;
      return;
    }

    if (motionReduced()) {
      this._input.placeholder = this._lines[0];
      return;
    }

    this._running = true;
    this._lineIndex = 0;
    this._charIndex = 0;
    this._input.placeholder = '';
    this.type();
  }

  stop(resetToDefault = true) {
    this._running = false;
    clearTimeout(this._timer);
    this._timer = null;
    if (resetToDefault && this._input) {
      this._input.placeholder = this._defaultPlaceholder;
    }
  }

  lock() {
    if (this._locked && !this._running) return;
    this._locked = true;
    this.stop(true);
  }

  type() {
    if (!this._running || this._locked) return;

    const line = this._lines[this._lineIndex];
    if (!line) return;

    if (this._charIndex < line.length) {
      this._charIndex += 1;
      this._input.placeholder = line.slice(0, this._charIndex);
      this._timer = setTimeout(() => this.type(), PLACEHOLDER_TYPE_MS);
      return;
    }

    this._timer = setTimeout(() => this.erase(), PLACEHOLDER_HOLD_MS);
  }

  erase() {
    if (!this._running || this._locked) return;

    const line = this._lines[this._lineIndex];
    if (!line) return;

    if (this._charIndex > 0) {
      this._charIndex -= 1;
      this._input.placeholder = line.slice(0, this._charIndex);
      this._timer = setTimeout(() => this.erase(), PLACEHOLDER_ERASE_MS);
      return;
    }

    this._lineIndex = (this._lineIndex + 1) % this._lines.length;
    this._timer = setTimeout(() => this.type(), PLACEHOLDER_TYPE_MS);
  }
}

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this._onResetClick = (e) => this.resetSearch(e);
    this._debouncedOnChange = debounce(() => this.onQueryInput(), DEBOUNCE_MS);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._input = this.querySelector('input[type="search"]');
    this._resetButton = this.querySelector('button[type="reset"]');
    this._searchDrawer = this.closest('search-drawer');

    if (!this._searchDrawer) return;

    this._searchRecommendations = this._searchDrawer.querySelector('.search-recommendations');
    this._searchContent = this._searchDrawer.querySelector('.search-results');
    this._recentSearch = this._searchDrawer.querySelector('[data-recent-search]');
    this._recentSearchItems = this._recentSearch?.querySelector('[data-recent-search-items]');
    this._resultsEl = this._searchDrawer.querySelector(`#${RESULTS_ID}`);
    this._runningPlaceholder = new RunningPlaceholder(this._input, this.dataset.runningSearchPlaceholder);
    this._runningPlaceholder.bindLockListeners();

    this._resetButton.addEventListener('click', this._onResetClick);
    this._input.addEventListener('input', this._debouncedOnChange);
    this.addEventListener('submit', this.onFormSubmit.bind(this));
    this.resetSearch(null, { focus: false, syncRecent: false });
  }

  disconnectedCallback() {
    this.stopRunningPlaceholder();
    this._runningPlaceholder?.unbindLockListeners();
    this._resetButton?.removeEventListener('click', this._onResetClick);
    this._input?.removeEventListener('input', this._debouncedOnChange);
    this._initialized = false;
  }

  startRunningPlaceholder() {
    this._runningPlaceholder?.start();
  }

  stopRunningPlaceholder(resetToDefault = true) {
    this._runningPlaceholder?.stop(resetToDefault);
  }

  lockRunningPlaceholder() {
    this._runningPlaceholder?.lock();
  }

  onFormSubmit() {
    const query = this.readQuery();
    if (!query) {
      // this.resetSearch();
      return;
    }
    this.saveRecentQuery(query);
  }

  onQueryInput() {
    const query = this.readQuery();
    if (!query) {
      this.resetSearch();
      return;
    }

    // this.saveRecentQuery(query);
    this.showResults(this.buildSuggestUrl().toString());
  }

  showResults(url) {
    if (this.cachedMap.has(url)) {
      this.applyResultsHtml(this.cachedMap.get(url));
      this.setAttribute('results', '');
      return;
    }

    this.fetchResults(url);
  }

  fetchResults(url) {
    this.setAttribute('loading', 'true');
    this._resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.applyResultsHtml(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        this.removeAttribute('loading');
        this._resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }

  applyResultsHtml(responseText) {
    if (!this._resultsEl) return;

    const contentElement = new DOMParser()
      .parseFromString(responseText, 'text/html')
      .getElementById(RESULTS_ID);

    if (!contentElement) {
      console.error(`Element with id '${RESULTS_ID}' not found in the parsed response.`);
      return;
    }

    this._searchRecommendations?.classList.add('hidden');
    this._recentSearch?.classList.add('hidden');
    this._searchContent?.classList.remove('hidden');
    this._resetButton.classList.remove('hidden');
    this._resultsEl.innerHTML = contentElement.innerHTML;
  }

  readRecentQueries() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY)) || [];
    } catch {
      return [];
    }
  }

  saveRecentQuery(query) {
    const searches = this.readRecentQueries()
      .filter((item) => item.toLowerCase() !== query.toLowerCase());
    searches.unshift(query);
    localStorage.setItem(
      RECENT_SEARCHES_KEY,
      JSON.stringify(searches.slice(0, MAX_RECENT_SEARCHES))
    );
  }

  buildSuggestUrl() {
    const url = new URL(`${window.shopUrl}${routes.predictive_search_url}`);
    url.searchParams.set('q', this.readQuery());
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', 'search-drawer');
    return url;
  }

  syncRecentSearches() {
    if (!this._recentSearch) return;

    const searches = this.readRecentQueries();
    if (!searches.length) {
      if (this._recentSearchItems) this._recentSearchItems.innerHTML = '';
      this._recentSearch.classList.add('hidden');
      return;
    }

    if (this._recentSearchItems) {
      this._recentSearchItems.innerHTML = searches
        .map(
          (search) =>
            `<a class="recent-search-item" href="${routes.search_url}?type=product&q=${encodeURIComponent(search)}">${search}</a>`
        )
        .join('');
    }
    this._recentSearch.classList.remove('hidden');
  }

  prepareOnOpen() {
    this.syncRecentSearches();
  }

  resetSearch(event, { focus = true, syncRecent = true } = {}) {
    if (event instanceof Event) event.preventDefault();

    this._searchContent?.classList.add('hidden');
    this._searchRecommendations?.classList.remove('hidden');

    if (syncRecent) this.syncRecentSearches();

    this._resetButton.classList.add('hidden');
    this._input.value = '';
    if (event instanceof Event) this.lockRunningPlaceholder();
    if (focus) this._input.focus();
    this.removeAttribute('results');
  }

  readQuery() {
    return this._input.value.trim();
  }
}

customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

if (!customElements.get('search-drawer')) {
  class SearchDrawer extends HTMLElement {
    constructor() {
      super();
      this._handleEscapeKey = (event) => {
        if (event.code === 'Escape') this.close();
      };
      this._handleCloseClick = (event) => {
        if (event.target.closest('[is="close-drawer"]')) this.close();
      };
      this._handleTriggerClick = (event) => {
        event.preventDefault();
        this.open(event.currentTarget);
      };

      this._hoverTimer = null;
      this._handleMouseEnter = (event) => {
        const trigger = event.currentTarget;
        this._hoverTimer = setTimeout(() => {
          this.open(trigger);
        }, DEBOUNCE_MS);
      };

      this._handleMouseLeave = () => {
        clearTimeout(this._hoverTimer);
      };

      this._handleTriggerKeydown = (event) => {
        if (event.code === 'Space' || event.code === 'Enter') {
          event.preventDefault();
          this.open(event.currentTarget);
        }
      };

      this._handleHoverPreventClick = (event) => {
        event.preventDefault();
      };
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._closeBtn = this.querySelector('#search-drawer-close-btn');
      this._input = this.querySelector('input[type="search"]');
      this._focusTarget = this._input || this._closeBtn;
      this._searchTriggers = Array.from(document.querySelectorAll('[aria-controls="SearchDrawer"]'));
      this._searchTrigger = document.querySelector('#headerIconSearch') || this._searchTriggers[0];
      this._predictiveSearch = this.querySelector('[is="predictive-search"]');

      this.setClosedAccessibility();

      this.addEventListener('keyup', this._handleEscapeKey);
      this.addEventListener('click', this._handleCloseClick);

      const supportsHover = window.matchMedia('(hover: hover)').matches;

      this._searchTriggers.forEach((trigger) => {
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-haspopup', 'dialog');

        const triggerType = trigger.dataset.openDrawer || 'click';
        if (triggerType === 'hover' && supportsHover) {
          trigger.addEventListener('click', this._handleHoverPreventClick);
          trigger.addEventListener('mouseenter', this._handleMouseEnter);
          trigger.addEventListener('mouseleave', this._handleMouseLeave);
        } else {
          trigger.addEventListener('click', this._handleTriggerClick);
        }
        trigger.addEventListener('keydown', this._handleTriggerKeydown);
      });
    }

    setClosedAccessibility() {
      setDrawerClosedAccessibility(this);
    }

    setOpenAccessibility() {
      setDrawerOpenAccessibility(this);
    }

    disconnectedCallback() {
      this._predictiveSearch?.stopRunningPlaceholder?.();
      this.removeEventListener('keyup', this._handleEscapeKey);
      this.removeEventListener('click', this._handleCloseClick);

      this._searchTriggers?.forEach((trigger) => {
        trigger.removeEventListener('click', this._handleTriggerClick);
        trigger.removeEventListener('click', this._handleHoverPreventClick);
        trigger.removeEventListener('keydown', this._handleTriggerKeydown);
        trigger.removeEventListener('mouseenter', this._handleMouseEnter);
        trigger.removeEventListener('mouseleave', this._handleMouseLeave);
      });

      this._initialized = false;
    }

    setTriggerExpanded(expanded) {
      this._searchTriggers?.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    open(triggerElement) {
      if (triggerElement) this.previousFocusedElement = triggerElement;
      clearTimeout(this._searchCloseTimer);
      clearTimeout(this._searchOpenTimer);
      this.cancelPendingFocus();
      this.setOpenAccessibility();
      this.setAttribute('open', '');
      this.setAttribute('active', '');
      this.setTriggerExpanded(true);
      this._predictiveSearch?.prepareOnOpen?.();
      this._searchOpenTimer = setTimeout(() => {
        this.setAttribute('search-open', '');
        this._predictiveSearch?.startRunningPlaceholder?.();
        this.focusAfterBodyTransition();
      }, 500);
      lockScroll();
    }

    cancelPendingFocus() {
      clearTimeout(this._searchFocusTimer);
      this._searchFocusTimer = null;
      if (this._onBodyTransitionEnd) {
        this.querySelector('.search-drawer-body')?.removeEventListener(
          'transitionend',
          this._onBodyTransitionEnd
        );
        this._onBodyTransitionEnd = null;
      }
    }

    focusAfterBodyTransition() {
      const focusNow = () => {
        if (!this.hasAttribute('active')) return;
        trapFocus(this, this._focusTarget);
      };

      const body = this.querySelector('.search-drawer-body');
      if (!body || motionReduced()) {
        focusNow();
        return;
      }

      this._onBodyTransitionEnd = (event) => {
        if (event.target !== body) return;
        if (event.propertyName !== 'transform') return;
        this.cancelPendingFocus();
        focusNow();
      };

      body.addEventListener('transitionend', this._onBodyTransitionEnd);
      // Body: transition-delay 0.7s + duration 0.6s = 1.3s after search-open
      this._searchFocusTimer = setTimeout(() => {
        this.cancelPendingFocus();
        focusNow();
      }, 1400);
    }

    close() {
      clearTimeout(this._searchOpenTimer);
      this.cancelPendingFocus();
      this._predictiveSearch?.stopRunningPlaceholder?.();
      closeDrawer(this);
      this.setTriggerExpanded(false);
      this.removeAttribute('search-open');
      clearTimeout(this._searchCloseTimer);
      this._searchCloseTimer = setTimeout(() => {
        if (!this.hasAttribute('active')) {
          // this.removeAttribute('search-open'); // disabled, because now no need of delay. Now attribute removed immediately. 
        }
      }, 300);
      const suppressReturn = this._suppressReturnFocus === true;
      this._suppressReturnFocus = false;
      if (suppressReturn) {
        removeTrapFocus(null, { restoreFocus: false });
      } else {
        removeTrapFocus(this.previousFocusedElement);
      }
      this.setClosedAccessibility();
      unlockScroll();
    }
  }

  customElements.define('search-drawer', SearchDrawer);
}

function blurHeaderSearchIfAutofocused() {
  const input = document.getElementById('HeaderSearchInput');
  if (!input) return;

  // Mobile browsers can autofocus the header search field on load and open the keyboard.
  if (document.activeElement === input) {
    input.blur();
  }
}

const HEADER_PLACEHOLDER_MQ = '(max-width: 1024px)';

function initHeaderRunningPlaceholder() {
  const input = document.getElementById('HeaderSearchInput');
  if (!input || input._runningPlaceholder) return;

  blurHeaderSearchIfAutofocused();

  const source = input.dataset.runningSearchPlaceholder;
  if (!source) return;

  const runningPlaceholder = new RunningPlaceholder(input, source);
  input._runningPlaceholder = runningPlaceholder;

  // Readonly drawer trigger should keep animating; editable inputs lock on interaction
  if (!input.readOnly) {
    runningPlaceholder.bindLockListeners();
  }

  const media = window.matchMedia(HEADER_PLACEHOLDER_MQ);
  const syncPlaceholder = () => {
    if (media.matches) {
      runningPlaceholder.start();
      return;
    }
    runningPlaceholder.stop(true);
  };

  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', syncPlaceholder);
  } else {
    media.addListener(syncPlaceholder);
  }

  syncPlaceholder();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    blurHeaderSearchIfAutofocused();
    initHeaderRunningPlaceholder();
  });
} else {
  blurHeaderSearchIfAutofocused();
  initHeaderRunningPlaceholder();
}

window.addEventListener('pageshow', blurHeaderSearchIfAutofocused);
